import {
  buildConventionalScopeBody,
  ConventionalScope,
  conventionalScopeKey,
  coreApi,
  BLUEPRINT_FAMILY_KEEP_UNUSED_DATA_FOR_SECONDS,
  BlueprintFamilyTrendsWindowDays,
} from '../core'

// How the momentum rows are grouped. Maps to the BE `groupBy` query enum on
// POST /v2/blueprint-families/momentum. Default `FAMILY` keys rows by blueprint
// family; the other two key by event group / event type and scope the population
// to that group.
//
// Do NOT sum child-family momentum to get a group's momentum — a game adopting two
// families in the window is counted twice. On prod, Monetization reads +28 games
// (+31.8 pp) summed versus +0 computed over the union of its games. That is what
// `groupBy` is for. There is no family-scoped aggregate in the grouped modes; the
// family drill-down already shows per-family momentum.
export type BlueprintFamilyMomentumGroupBy = 'FAMILY' | 'EVENT_GROUP' | 'EVENT_TYPE'

// Response shape for POST /blueprint-families/momentum.
// Mirrors BE BlueprintFamilyMomentumRespDto.
//
// `momentum` is the signed change in adopting games across the selected period, counted
// over a MATCHED COHORT: cohort games active during the period's last month, minus cohort
// games active during its first month. Those are the adoption chart's last and first
// points, so `momentum` equals the last point's `distinctGamesCount` minus the first's.
// At `windowDays=30` both months are the same month and `momentum` is always `0`. A game
// active in both months counts on both sides, and a game active only in the months
// between them does not count: this is a net figure, not a count of games that moved.
//
// Percentage-point change is `momentum / cohortGamesCount * 100`. Dividing by any other
// population recreates the >100pt bug this endpoint exists to fix, which is why
// `cohortGamesCount` rides on the response — it can be 0.
//
// This is the SAME `cohortGamesCount` the list, detail and rollup responses now carry for
// the same request, so the Adoption tile and the Change tile finally share one denominator.
//
// The matched cohort is also why the earlier window does NOT use its own tracked set:
// counting each window over its own denominator can never return "no change" (0 of 151
// families read as unchanged under that definition on staging), which is fatal for a
// metric this sticky. The matched cohort gives 66 positive / 82 zero / 3 negative.
export type BlueprintFamilyMomentumApiItem = {
  id: string
  momentum: number
  cohortGamesCount: number
}

type GetBlueprintFamilyMomentumQueryParams = {
  groupBy?: BlueprintFamilyMomentumGroupBy
  windowDays?: BlueprintFamilyTrendsWindowDays
} & ConventionalScope

const momentumApi = coreApi.injectEndpoints({
  endpoints: (builder) => ({
    // POST so segment scoping can be sent as a `conventionalSubgenreIds` body list (BE @NotNull body).
    getBlueprintFamilyMomentum: builder.query<BlueprintFamilyMomentumApiItem[], GetBlueprintFamilyMomentumQueryParams>({
      keepUnusedDataFor: BLUEPRINT_FAMILY_KEEP_UNUSED_DATA_FOR_SECONDS,
      query: ({ groupBy, windowDays, ...scope } = {}) => ({
        url: 'blueprint-families/momentum',
        method: 'POST',
        params: {
          ...(groupBy !== undefined ? { groupBy } : {}),
          ...(windowDays !== undefined ? { windowDays } : {}),
        },
        body: buildConventionalScopeBody(scope),
      }),
      providesTags: (_result, _err, { groupBy, windowDays, ...scope } = {}) => [
        {
          type: 'BlueprintFamilies',
          id: `momentum/${groupBy ?? 'FAMILY'}/${windowDays ?? 'default'}/${conventionalScopeKey(scope)}`,
        },
      ],
    }),
  }),
})

export const { useGetBlueprintFamilyMomentumQuery } = momentumApi
